import type { ISPortfolioLabels } from './types'

/**
 * Textos del Portafolio Fase 2 (ENT-5952). Las llaves llevan el prefijo
 * `hubPortfolioPhase2` y se registran en `src/i18n/index.js` (es + en).
 * Cuidado: `i18n` usa `keySeparator: false` y `nsSeparator: ':'`, así que ninguna
 * llave ni plantilla de llave puede contener ':' ni '.'.
 *
 * Orden de cada fila: [llave, español, inglés]. Las plantillas conservan los
 * marcadores `{n}`, `{unit}`, `{currency}`, `{date}` que reemplaza la librería.
 */
const ROWS: Array<[string, string, string]> = [
  // Pestañas
  ['hubPortfolioPhase2TabPositions', 'Posiciones', 'Positions'],
  ['hubPortfolioPhase2TabDistribution', 'Distribución de portafolio', 'Portfolio distribution'],
  ['hubPortfolioPhase2TabPerformance', 'Rendimientos', 'Performance'],
  // Columnas
  ['hubPortfolioPhase2ColInstrument', 'Instrumento', 'Instrument'],
  ['hubPortfolioPhase2ColPurchaseDate', 'Fecha comp.', 'Purchase date'],
  ['hubPortfolioPhase2ColQuantity', '# títulos', '# shares'],
  ['hubPortfolioPhase2ColAverageCost', 'Costo prom.', 'Avg. cost'],
  ['hubPortfolioPhase2ColPrice', 'Precio', 'Price'],
  ['hubPortfolioPhase2ColUnitVariation', 'Var. U', 'Unit chg.'],
  ['hubPortfolioPhase2ColPercentageVariation', 'Var. %', 'Chg. %'],
  ['hubPortfolioPhase2ColMarketValue', 'Valor de mercado', 'Market value'],
  ['hubPortfolioPhase2ColTotalCost', 'Costo total', 'Total cost'],
  ['hubPortfolioPhase2ColPnl', 'P&L $', 'P&L $'],
  ['hubPortfolioPhase2ColPnlPct', 'P&L %', 'P&L %'],
  ['hubPortfolioPhase2ColDailyPnl', 'P&L diario $', 'Daily P&L $'],
  ['hubPortfolioPhase2ColDailyPnlPct', 'P&L diario %', 'Daily P&L %'],
  ['hubPortfolioPhase2ColBid', 'Compra', 'Bid'],
  ['hubPortfolioPhase2ColAsk', 'Venta', 'Ask'],
  ['hubPortfolioPhase2ColTime', 'Hora', 'Time'],
  ['hubPortfolioPhase2ColActions', 'Acciones', 'Actions'],
  // Totales y estados
  ['hubPortfolioPhase2DownloadPdf', 'Descargar PDF', 'Download PDF'],
  ['hubPortfolioPhase2Total', 'Total', 'Total'],
  ['hubPortfolioPhase2Multiple', 'Múltiples', 'Multiple'],
  ['hubPortfolioPhase2Undated', 'Sin fecha', 'No date'],
  ['hubPortfolioPhase2NoData', '—', '—'],
  ['hubPortfolioPhase2NoCategory', 'Sin categoría', 'Uncategorized'],
  ['hubPortfolioPhase2Others', 'Otros', 'Others'],
  // Sub-tabs de distribución (alcance)
  ['hubPortfolioPhase2ScopeAll', 'Todo el portafolio', 'Whole portfolio'],
  ['hubPortfolioPhase2ScopeEquity', 'Renta variable', 'Equity'],
  ['hubPortfolioPhase2ScopeFixedIncome', 'Renta fija', 'Fixed income'],
  // Dimensiones (chips)
  ['hubPortfolioPhase2DimInstrument', 'Instrumento', 'Instrument'],
  ['hubPortfolioPhase2DimSubAsset', 'Subactivos', 'Sub-assets'],
  ['hubPortfolioPhase2DimAssetType', 'Tipo activo', 'Asset type'],
  ['hubPortfolioPhase2DimCountry', 'Países', 'Countries'],
  ['hubPortfolioPhase2DimSector', 'Sector', 'Sector'],
  ['hubPortfolioPhase2DimRating', 'Calificación', 'Rating'],
  ['hubPortfolioPhase2DimTerm', 'Horizonte de inversión', 'Investment horizon'],
  ['hubPortfolioPhase2DimFixedIncomeType', 'Tipo de renta', 'Income type'],
  // Texto central de la dona
  ['hubPortfolioPhase2UnitSubAssetOne', 'subactivo', 'sub-asset'],
  ['hubPortfolioPhase2UnitSubAssetOther', 'subactivos', 'sub-assets'],
  ['hubPortfolioPhase2UnitCountryOne', 'país', 'country'],
  ['hubPortfolioPhase2UnitCountryOther', 'países', 'countries'],
  ['hubPortfolioPhase2UnitFixedIncomeTypeOne', 'tipo', 'type'],
  ['hubPortfolioPhase2UnitFixedIncomeTypeOther', 'tipos', 'types'],
  ['hubPortfolioPhase2CategoryCount', '{n} {unit}', '{n} {unit}'],
  ['hubPortfolioPhase2Participation', 'Participación', 'Share'],
  ['hubPortfolioPhase2ParticipationCurrency', 'Participación {currency}', 'Share {currency}'],
  ['hubPortfolioPhase2LoadingCompositions', 'Cargando composiciones…', 'Loading compositions…'],
  // Rendimientos
  ['hubPortfolioPhase2FilterAll', 'Todas', 'All'],
  ['hubPortfolioPhase2FilterWinners', 'Ganadoras', 'Winners'],
  ['hubPortfolioPhase2FilterLosers', 'Perdedoras', 'Losers'],
  ['hubPortfolioPhase2MetricPnlPct', 'P&L % individual', 'Individual P&L %'],
  ['hubPortfolioPhase2MetricPnlAmount', 'Contribución $', 'Contribution $'],
  ['hubPortfolioPhase2KpiMarketValue', 'Valor de mercado', 'Market value'],
  ['hubPortfolioPhase2KpiTotalCost', 'Costo total', 'Total cost'],
  ['hubPortfolioPhase2KpiPnl', 'P&L $', 'P&L $'],
  ['hubPortfolioPhase2KpiPnlPct', 'P&L %', 'P&L %'],
  ['hubPortfolioPhase2DetailPnl', 'P&L $', 'P&L $'],
  ['hubPortfolioPhase2DetailPnlPct', 'P&L %', 'P&L %'],
  ['hubPortfolioPhase2DetailContribution', '% de contribución', '% contribution'],
  ['hubPortfolioPhase2DetailAverageCost', 'Costo promedio', 'Average cost'],
  ['hubPortfolioPhase2DetailShow', 'Ver detalle', 'Show detail'],
  ['hubPortfolioPhase2DetailHide', 'Ocultar detalle', 'Hide detail'],
  // Lotes
  ['hubPortfolioPhase2LotAdd', 'Agregar lote', 'Add lot'],
  ['hubPortfolioPhase2LotSave', 'Guardar', 'Save'],
  ['hubPortfolioPhase2LotCancel', 'Cancelar', 'Cancel'],
  ['hubPortfolioPhase2LotRemove', 'Eliminar lote', 'Remove lot'],
  ['hubPortfolioPhase2LotExpand', 'Ver lotes', 'Show lots'],
  ['hubPortfolioPhase2LotCollapse', 'Ocultar lotes', 'Hide lots'],
  ['hubPortfolioPhase2LotRemoveInstrument', 'Eliminar instrumento', 'Remove instrument'],
  ['hubPortfolioPhase2LotDateAria', 'Fecha de compra', 'Purchase date'],
  ['hubPortfolioPhase2LotQuantityAria', 'Número de títulos', 'Number of shares'],
  ['hubPortfolioPhase2LotPriceAria', 'Precio de compra', 'Purchase price'],
  ['hubPortfolioPhase2LotN', 'Lote {n}', 'Lot {n}'],
  // Modal "Editar instrumentos" / crear portafolio (ENT-5932)
  ['hubPortfolioPhase2ModalCurrent', 'Actuales', 'Current'],
  ['hubPortfolioPhase2ModalAdd', 'Agregar', 'Add'],
  ['hubPortfolioPhase2ModalApply', 'Aplicar cambios', 'Apply changes'],
  ['hubPortfolioPhase2ModalPurchaseDate', 'Fecha compra', 'Purchase date'],
  ['hubPortfolioPhase2ModalAvgCost', 'Costo promedio', 'Average cost'],
  ['hubPortfolioPhase2ModalEmpty', 'Agrega instrumentos desde la pestaña "Agregar".', 'Add instruments from the "Add" tab.'],
  ['hubPortfolioPhase2ModalFixLots', 'Completa o corrige los lotes marcados.', 'Complete or fix the highlighted lots.'],
  [
    'hubPortfolioPhase2ModalConflict',
    'Algunos instrumentos cambiaron en otra sesión mientras editabas y no se modificaron. Ya ves la versión actual: revisa y vuelve a aplicar.',
    'Some instruments changed in another session while you were editing and were not modified. You now see the current version: review and apply again.'
  ],
  // Confirmación de borrado
  ['hubPortfolioPhase2DeleteTitle', 'Eliminar lote', 'Remove lot'],
  ['hubPortfolioPhase2DeleteMessageLot', '¿Quieres eliminar este lote?', 'Do you want to remove this lot?'],
  [
    'hubPortfolioPhase2DeleteMessageLastLot',
    'Es el último lote de este instrumento: al eliminarlo también se elimina el instrumento del portafolio.',
    'This is the last lot of the instrument: removing it also removes the instrument from the portfolio.'
  ],
  ['hubPortfolioPhase2DeleteConfirm', 'Eliminar', 'Remove'],
  ['hubPortfolioPhase2DeleteCancel', 'Cancelar', 'Cancel'],
  // Errores de validación
  [
    'hubPortfolioPhase2ErrQuantity',
    'Los títulos deben ser un número entero mayor a 0.',
    'Shares must be a whole number greater than 0.'
  ],
  ['hubPortfolioPhase2ErrPrice', 'El precio debe ser mayor a 0.', 'The price must be greater than 0.'],
  [
    'hubPortfolioPhase2ErrPriceDecimals',
    'El precio admite hasta 4 decimales.',
    'The price allows up to 4 decimal places.'
  ],
  [
    'hubPortfolioPhase2ErrDateFuture',
    'La fecha de compra no puede ser futura.',
    'The purchase date cannot be in the future.'
  ],
  ['hubPortfolioPhase2ErrDateInvalid', 'La fecha no es válida.', 'The date is not valid.'],
  [
    'hubPortfolioPhase2ErrUndatedDuplicate',
    'Sólo puede haber un lote sin fecha por instrumento.',
    'Only one undated lot is allowed per instrument.'
  ],
  ['hubPortfolioPhase2ErrSave', 'No se pudo guardar el lote.', 'The lot could not be saved.'],
  // Vacíos, aviso y última actualización
  ['hubPortfolioPhase2EmptyPositions', 'Aún no hay instrumentos en este portafolio.', 'There are no instruments in this portfolio yet.'],
  ['hubPortfolioPhase2EmptyDistribution', 'No hay datos para esta distribución.', 'There is no data for this distribution.'],
  ['hubPortfolioPhase2EmptyFilter', 'Ninguna posición cumple el filtro.', 'No position matches the filter.'],
  [
    'hubPortfolioPhase2Disclaimer',
    'Cálculo simplificado: rendimiento bruto sin dividendos, comisiones ni impuestos. Solo referencial.',
    'Simplified calculation: gross performance without dividends, fees or taxes. For reference only.'
  ],
  ['hubPortfolioPhase2LastUpdate', 'Última actualización {date}.', 'Last update {date}.'],
  // Categorías fijas de composición (no son literales: el adaptador las traduce)
  ['hubPortfolioPhase2AssetTypeFixedIncome', 'Renta Fija', 'Fixed Income'],
  ['hubPortfolioPhase2AssetTypeEquity', 'Renta Variable', 'Equity'],
  ['hubPortfolioPhase2AssetTypeCash', 'Efectivo', 'Cash'],
  ['hubPortfolioPhase2AssetTypeOther', 'Otros', 'Other'],
  ['hubPortfolioPhase2SubAssetEquities', 'Acciones', 'Stocks'],
  ['hubPortfolioPhase2SubAssetPreferred', 'Acciones preferentes', 'Preferred stocks'],
  ['hubPortfolioPhase2SubAssetEtfs', 'ETFs', 'ETFs'],
  ['hubPortfolioPhase2SubAssetBonds', 'Bonos', 'Bonds'],
  ['hubPortfolioPhase2SubAssetCash', 'Efectivo', 'Cash'],
  ['hubPortfolioPhase2SubAssetOther', 'Otros', 'Other'],
  ['hubPortfolioPhase2CountryMexico', 'México', 'Mexico'],
  ['hubPortfolioPhase2CountryUnitedStates', 'Estados Unidos', 'United States']
]

export const PORTFOLIO_PHASE2_I18N = {
  es: Object.fromEntries(ROWS.map(([key, es]) => [key, es])) as Record<string, string>,
  en: Object.fromEntries(ROWS.map(([key, , en]) => [key, en])) as Record<string, string>
}

export type TranslateFn = (key: string) => string

/** `ISPortfolioLabels` completo a partir de `t` (el `i18n.t` del Hub, que ya sigue el idioma activo). */
export function buildPortfolioLabels(t: TranslateFn): ISPortfolioLabels {
  const p = 'hubPortfolioPhase2'
  const k = (suffix: string) => t(`${p}${suffix}`)
  return {
    tabs: { positions: k('TabPositions'), distribution: k('TabDistribution'), performance: k('TabPerformance') },
    columns: {
      instrument: k('ColInstrument'),
      purchaseDate: k('ColPurchaseDate'),
      quantity: k('ColQuantity'),
      averageCost: k('ColAverageCost'),
      price: k('ColPrice'),
      unitVariation: k('ColUnitVariation'),
      percentageVariation: k('ColPercentageVariation'),
      marketValue: k('ColMarketValue'),
      totalCost: k('ColTotalCost'),
      pnl: k('ColPnl'),
      pnlPct: k('ColPnlPct'),
      dailyPnl: k('ColDailyPnl'),
      dailyPnlPct: k('ColDailyPnlPct'),
      bid: k('ColBid'),
      ask: k('ColAsk'),
      time: k('ColTime'),
      actions: k('ColActions')
    },
    total: k('Total'),
    multiple: k('Multiple'),
    undated: k('Undated'),
    noData: k('NoData'),
    noCategory: k('NoCategory'),
    others: k('Others'),
    scopes: { all: k('ScopeAll'), equity: k('ScopeEquity'), fixedIncome: k('ScopeFixedIncome') },
    dimensions: {
      instrument: k('DimInstrument'),
      subAsset: k('DimSubAsset'),
      assetType: k('DimAssetType'),
      country: k('DimCountry'),
      sector: k('DimSector'),
      rating: k('DimRating'),
      term: k('DimTerm'),
      fixedIncomeType: k('DimFixedIncomeType')
    },
    categoryUnits: {
      subAsset: { one: k('UnitSubAssetOne'), other: k('UnitSubAssetOther') },
      country: { one: k('UnitCountryOne'), other: k('UnitCountryOther') },
      fixedIncomeType: { one: k('UnitFixedIncomeTypeOne'), other: k('UnitFixedIncomeTypeOther') }
    },
    categoryCount: k('CategoryCount'),
    participation: k('Participation'),
    participationCurrency: k('ParticipationCurrency'),
    downloadPdf: k('DownloadPdf'),
    loadingCompositions: k('LoadingCompositions'),
    filters: { all: k('FilterAll'), winners: k('FilterWinners'), losers: k('FilterLosers') },
    metrics: { pnlPct: k('MetricPnlPct'), pnlAmount: k('MetricPnlAmount') },
    kpis: {
      marketValue: k('KpiMarketValue'),
      totalCost: k('KpiTotalCost'),
      pnl: k('KpiPnl'),
      pnlPct: k('KpiPnlPct')
    },
    detail: {
      pnl: k('DetailPnl'),
      pnlPct: k('DetailPnlPct'),
      contribution: k('DetailContribution'),
      averageCost: k('DetailAverageCost'),
      showDetail: k('DetailShow'),
      hideDetail: k('DetailHide')
    },
    lot: {
      add: k('LotAdd'),
      save: k('LotSave'),
      cancel: k('LotCancel'),
      remove: k('LotRemove'),
      expand: k('LotExpand'),
      collapse: k('LotCollapse'),
      removeInstrument: k('LotRemoveInstrument'),
      dateAria: k('LotDateAria'),
      quantityAria: k('LotQuantityAria'),
      priceAria: k('LotPriceAria'),
      lotN: k('LotN')
    },
    deleteDialog: {
      title: k('DeleteTitle'),
      messageLot: k('DeleteMessageLot'),
      messageLastLot: k('DeleteMessageLastLot'),
      confirm: k('DeleteConfirm'),
      cancel: k('DeleteCancel')
    },
    errors: {
      QUANTITY_NOT_POSITIVE_INTEGER: k('ErrQuantity'),
      PRICE_NOT_POSITIVE: k('ErrPrice'),
      PRICE_TOO_MANY_DECIMALS: k('ErrPriceDecimals'),
      DATE_IN_FUTURE: k('ErrDateFuture'),
      DATE_INVALID: k('ErrDateInvalid'),
      UNDATED_LOT_DUPLICATE: k('ErrUndatedDuplicate'),
      save: k('ErrSave')
    },
    empty: { positions: k('EmptyPositions'), distribution: k('EmptyDistribution'), filter: k('EmptyFilter') },
    disclaimer: k('Disclaimer'),
    lastUpdate: k('LastUpdate')
  }
}
